/**
 * A Visão geral da administração (docs/spec/admin.md): os números do servidor agora. Os de contas e de
 * planos levam a Usuários já filtrados; o de mesas, à seção Mesas.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { api } from '../api.js';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import { PLAN_IDS, type AdminOverview as Overview, type AdminPlanFilter, type AdminUserFilter } from '../shared.js';
import { usePlanName } from '../plans/usePlanName.js';
import { describeError, useApp } from '../store.js';
import { Spinner } from '../ui/controls.js';

export function AdminOverview({
  version,
  onOpenUsers,
  onOpenRooms,
}: {
  version: number;
  onOpenUsers: (preset: Partial<{ filter: AdminUserFilter; plan: AdminPlanFilter }>) => void;
  onOpenRooms: () => void;
}) {
  const t = useT();
  const token = useApp((s) => s.token);
  const planName = usePlanName();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.adminOverview(token).then(
      (result) => {
        if (!alive) return;
        setOverview(result);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  if (error) {
    return (
      <p className="rd-adminUsers__error" role="alert">
        {error}
      </p>
    );
  }
  if (!overview) {
    return (
      <div className="rd-placeholder">
        <Spinner size={28} />
      </div>
    );
  }
  const { accounts, storage, rooms, uploads } = overview;
  return (
    <section className="rd-adminOverview" aria-label={t('admin.section.overview')}>
      <h3 className="rd-adminOverview__group">{t('admin.overview.accounts')}</h3>
      <div className="rd-adminOverview__tiles">
        <Tile label={t('admin.overview.total')} value={String(accounts.total)} hint={t('admin.overview.newLastWeek', { n: accounts.newLastWeek })} onOpen={() => onOpenUsers({})} />
        <Tile label={t('admin.overview.online')} value={String(accounts.online)} onOpen={() => onOpenUsers({ filter: 'online' })} />
        <Tile label={t('admin.overview.suspended')} value={String(accounts.suspended)} onOpen={() => onOpenUsers({ filter: 'suspended' })} />
        <Tile label={t('admin.overview.admins')} value={String(accounts.admins)} onOpen={() => onOpenUsers({ filter: 'admins' })} />
      </div>
      <h3 className="rd-adminOverview__group">{t('admin.overview.plans')}</h3>
      <div className="rd-adminOverview__tiles">
        {PLAN_IDS.map((id) => (
          <Tile key={id} label={planName(id)} value={String(overview.plans[id] ?? 0)} onOpen={() => onOpenUsers({ plan: id })} />
        ))}
      </div>
      <h3 className="rd-adminOverview__group">{t('admin.overview.server')}</h3>
      <div className="rd-adminOverview__tiles">
        <Tile label={t('admin.overview.space')} value={formatBytes(storage.usedBytes)} hint={t('admin.overview.files', { n: storage.files })} />
        <Tile label={t('admin.overview.rooms')} value={String(rooms.total)} hint={t('admin.overview.activeRooms', { n: rooms.active })} onOpen={onOpenRooms} />
        <Tile label={t('admin.overview.uploads')} value={formatBytes(uploads.memoryInUse)} hint={t('admin.overview.uploadsLimit', { limit: formatBytes(uploads.memoryLimit) })} />
      </div>
    </section>
  );
}

/** Um número da Visão geral; com `onOpen`, é um botão que leva à lista de onde ele vem. */
function Tile({ label, value, hint, onOpen }: { label: string; value: string; hint?: string; onOpen?: () => void }) {
  const body: ReactNode = (
    <>
      <span className="rd-adminTile__label">{label}</span>
      <strong className="rd-adminTile__value">{value}</strong>
      {hint ? <span className="rd-adminTile__hint">{hint}</span> : null}
    </>
  );
  return onOpen ? (
    <button type="button" className="rd-adminTile is-link" onClick={onOpen}>
      {body}
    </button>
  ) : (
    <div className="rd-adminTile">{body}</div>
  );
}
